import type { AuthSource, StorageLike } from "./providerAuth.ts";

export type ProviderSessionRestoreAction = "none" | "restore" | "clear";

interface ProviderSessionRestoreState {
  version: 1;
  returnTo: string;
  logoutEpoch: string;
  createdAt: number;
}

interface ProviderSessionRestoreInput {
  authSource: AuthSource;
  isAuthenticated: boolean;
  hasTokens: boolean;
  providerEnabled: boolean;
  restorePending: boolean;
  accountUuid: string | null;
}

const providerSessionRestoreKey = "wenlix-provider-session-restore-v1";
export const providerLogoutEpochStorageKey = "wenlix-provider-logout-epoch-v1";
const canonicalUuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

function safeReturnPath(value: string): string {
  if (!value.startsWith("/") || value.startsWith("//")) return "/dashboard";
  let parsed: URL;
  try {
    parsed = new URL(value, "https://wenlix.invalid");
  } catch {
    return "/dashboard";
  }
  if (parsed.origin !== "https://wenlix.invalid" || parsed.pathname === "/auth/callback") {
    return "/dashboard";
  }
  const sensitiveQueryName = /^(access_token|refresh_token|id_token|token|code|authorization)$/i;
  if ([...parsed.searchParams.keys()].some((name) => sensitiveQueryName.test(name))) {
    return "/dashboard";
  }
  return `${parsed.pathname}${parsed.search}`;
}

export function providerSessionRestoreAction(
  input: ProviderSessionRestoreInput,
): ProviderSessionRestoreAction {
  const missingProviderTokens = input.authSource === "provider" &&
    input.isAuthenticated && !input.hasTokens;
  if (!missingProviderTokens) return "none";
  if (!input.providerEnabled || input.restorePending ||
    !input.accountUuid || !canonicalUuidPattern.test(input.accountUuid)) return "clear";
  return "restore";
}

export function currentProviderLogoutEpoch(storage: StorageLike): string {
  const stored = storage.getItem(providerLogoutEpochStorageKey);
  return stored && /^[A-Za-z0-9._:-]{1,128}$/.test(stored) ? stored : "initial";
}

export function invalidateProviderSessionRestores(
  storage: StorageLike,
  nextEpoch: string = globalThis.crypto.randomUUID(),
): void {
  if (!/^[A-Za-z0-9._:-]{1,128}$/.test(nextEpoch)) {
    throw new Error("provider logout epoch is invalid");
  }
  storage.setItem(providerLogoutEpochStorageKey, nextEpoch);
}

export function isProviderLogoutEpochStorageKey(key: string | null): boolean {
  return key === providerLogoutEpochStorageKey;
}

export function markProviderSessionRestore(
  storage: StorageLike,
  returnTo: string,
  logoutEpoch: string,
  now: number = Date.now(),
): void {
  if (!/^[A-Za-z0-9._:-]{1,128}$/.test(logoutEpoch)) {
    throw new Error("provider logout epoch is invalid");
  }
  storage.setItem(providerSessionRestoreKey, JSON.stringify({
    version: 1,
    returnTo: safeReturnPath(returnTo),
    logoutEpoch,
    createdAt: now,
  } satisfies ProviderSessionRestoreState));
}

export function providerSessionRestorePending(storage: StorageLike): boolean {
  return storage.getItem(providerSessionRestoreKey) !== null;
}

export function clearProviderSessionRestore(storage: StorageLike): void {
  storage.removeItem(providerSessionRestoreKey);
}

function readProviderSessionRestore(storage: StorageLike): ProviderSessionRestoreState | null {
  const stored = storage.getItem(providerSessionRestoreKey);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored) as Partial<ProviderSessionRestoreState>;
    if (parsed.version !== 1 || typeof parsed.returnTo !== "string" ||
      typeof parsed.logoutEpoch !== "string" ||
      !/^[A-Za-z0-9._:-]{1,128}$/.test(parsed.logoutEpoch) ||
      typeof parsed.createdAt !== "number" || !Number.isFinite(parsed.createdAt)) {
      return null;
    }
    return parsed as ProviderSessionRestoreState;
  } catch {
    return null;
  }
}

export function providerSessionRestoreMatchesLogoutEpoch(
  sessionStorage: StorageLike,
  sharedStorage: StorageLike,
): boolean {
  const restore = readProviderSessionRestore(sessionStorage);
  return restore !== null && restore.logoutEpoch === currentProviderLogoutEpoch(sharedStorage);
}

export function consumeProviderSessionRestoreReturnTo(storage: StorageLike): string {
  const parsed = readProviderSessionRestore(storage);
  storage.removeItem(providerSessionRestoreKey);
  return parsed ? safeReturnPath(parsed.returnTo) : "/dashboard";
}
